import React, { useEffect } from 'react';
import { Box, Grid, Typography, styled } from '@mui/material';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import CategorySelect from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/components/informationSelect/CategorySelect';
import HrSelect from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/components/informationSelect/HrSelect';
import MinMaxSelect from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/components/informationSelect/MinMaxSelect';
import { useGetIFSEInformation } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/hooks/ifseInformation';
import { filterGradeLines } from './helpers';
import { useTranslation } from 'react-i18next';

const StyBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

type Props = {
    cadreEmploi: string;
    category: string;
    editMode?: boolean;
    filiere: string;
    hrSimulationId: number;
    jobs: string[];
    maxValue: number | undefined;
    minValue: number | undefined;
    setCadreEmploi: (value: string) => void;
    setCategory: (value: string) => void;
    setFiliere: (value: string) => void;
    setJobs: (value: string[]) => void;
    setMaxValue: (value: number | undefined) => void;
    setMinValue: (value: number | undefined) => void;
};

const IfseInformationStep = ({
    cadreEmploi,
    category,
    editMode = false,
    filiere,
    hrSimulationId,
    jobs,
    maxValue,
    minValue,
    setCadreEmploi,
    setCategory,
    setFiliere,
    setJobs,
    setMaxValue,
    setMinValue,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { data: freeHypothesisResponse, isLoading: isDataLoading } =
        useGetIFSEInformation(hrSimulationId);
    const { categories: categoryList = [], gradeLines } =
        freeHypothesisResponse ?? {};

    const {
        categories: allowedCategories,
        filieres: filieresList,
        cadresEmploi: cadresEmploiList,
        jobsLabel: jobsLabelList,
    } = filterGradeLines({
        gradeLines: gradeLines || [],
        category,
        filiere,
        cadreEmploi,
        jobs,
    });

    // By default the selected category is the first
    useEffect(() => {
        if (!editMode) {
            setCategory(categoryList[0]);
        }
    }, [categoryList]);

    return (
        <StyBox sx={{ minWidth: '10rem' }}>
            <StyPaper sx={{ padding: '12px' }}>
                <Typography
                    variant="h4"
                    sx={{ paddingBottom: '12px', fontSize: '0.9rem' }}
                >
                    {t(
                        'humanResources.simulation.freeHypothesis.ifse.filterAgentsOn',
                    )}
                </Typography>
                <CategorySelect
                    category={category}
                    setCategory={setCategory}
                    data={categoryList}
                    allowedCategories={allowedCategories}
                    isLoading={isDataLoading}
                />
                <Grid container spacing={4} sx={{ paddingTop: '12px' }}>
                    <Grid item xs={6}>
                        <HrSelect
                            data={filiere}
                            setData={setFiliere}
                            dataList={filieresList}
                            isLoading={isDataLoading}
                            title={t(
                                'humanResources.simulation.freeHypothesis.ifse.filiere',
                            )}
                            placeholder={t(
                                'humanResources.simulation.freeHypothesis.ifse.selectFiliere',
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <HrSelect
                            data={cadreEmploi}
                            setData={setCadreEmploi}
                            dataList={cadresEmploiList}
                            isLoading={isDataLoading}
                            title={t(
                                'humanResources.simulation.freeHypothesis.ifse.cadreEmploi',
                            )}
                            placeholder={t(
                                'humanResources.simulation.freeHypothesis.ifse.selectCadreEmploi',
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <HrSelect
                            data={jobs}
                            setData={setJobs}
                            dataList={jobsLabelList}
                            isLoading={isDataLoading}
                            multiple
                            title={t(
                                'humanResources.simulation.freeHypothesis.ifse.job',
                            )}
                            placeholder={t(
                                'humanResources.simulation.freeHypothesis.ifse.selectJob',
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MinMaxSelect
                            minValue={minValue}
                            setMinValue={setMinValue}
                            maxValue={maxValue}
                            setMaxValue={setMaxValue}
                        />
                    </Grid>
                </Grid>
            </StyPaper>
        </StyBox>
    );
};

export default IfseInformationStep;
